import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * Interface for template dropdown item
 */
export interface TemplateDropdownItem {
  id: string;
  name: string;
  description?: string;
  channelId: string;
  status: 'draft' | 'pending_approval' | 'approved' | 'rejected' | 'archived';
}

/**
 * Hook to fetch templates for dropdown
 *
 * Only returns APPROVED templates by default
 * Can be filtered by channelId to show templates for specific channel
 *
 * @param channelId - Optional channel ID to filter templates
 * @returns Query result with list of templates
 *
 * @example
 * ```tsx
 * // All approved templates
 * const { data: templates, isLoading } = useTemplatesDropdown();
 *
 * // Templates for specific channel
 * const { data: templates } = useTemplatesDropdown(selectedChannelId);
 *
 * <select>
 *   {templates?.map(template => (
 *     <option key={template.id} value={template.id}>
 *       {template.name}
 *     </option>
 *   ))}
 * </select>
 * ```
 */
export const useTemplatesDropdown = (channelId?: string, options?: { enabled?: boolean }) => {
  // Default enabled to true (fetch all templates when no channelId)
  // Can be overridden via options.enabled for dependent dropdown patterns
  const isEnabled = options?.enabled !== undefined ? options.enabled : true;

  return useQuery({
    queryKey: ['templates-dropdown', channelId],
    queryFn: async () => {
      // Build URL with optional channelId parameter
      const url = channelId
        ? `/templates/dropdown?channelId=${channelId}`
        : '/templates/dropdown';

      const response = await api.get(url);

      // Backend returns { success: true, data: [...] }
      return response.data.data as TemplateDropdownItem[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - templates change infrequently
    refetchOnWindowFocus: false,
    enabled: isEnabled,
  });
};
